import { ref, watch } from 'vue'
import { fetchPropertiesFromApi } from '@/services/propertiesApi'
import { normalizePropertyCollection } from '@/utils/propertyPayload'

/**
 * Composable for search autocomplete functionality.
 * Debounces API calls and returns property suggestions.
 */
export function useSearchAutocomplete(options = {}) {
  const {
    debounceMs = 300,
    minChars = 2,
    maxResults = 6
  } = options

  const query = ref('')
  const suggestions = ref([])
  const isLoading = ref(false)
  const isOpen = ref(false)
  const error = ref(null)

  let debounceTimer = null
  let abortController = null

  const clearSuggestions = () => {
    suggestions.value = []
    isOpen.value = false
  }

  const fetchSuggestions = async (searchTerm) => {
    // Cancel any pending request
    if (abortController) {
      abortController.abort()
    }

    const trimmed = (searchTerm || '').trim()
    
    if (trimmed.length < minChars) {
      clearSuggestions()
      return
    }

    isLoading.value = true
    error.value = null
    abortController = new AbortController()

    try {
      const result = await fetchPropertiesFromApi({
        page: 1,
        perPage: maxResults,
        filters: { search: trimmed }
      })

      // Only update if this is still the current query
      if (query.value.trim() === trimmed) {
        // Normalize raw API response to consistent property objects
        const normalized = normalizePropertyCollection(result.items || [])
        suggestions.value = normalized.map(property => {
          // Extract displayable location string
          const loc = property.location || {}
          const locationText = typeof loc === 'string' 
            ? loc 
            : [loc.area, loc.city, loc.state].filter(Boolean).join(', ') || loc.address || ''
          
          return {
            id: property.id,
            name: property.name,
            location: locationText,
            image: property.thumbnailUrl || property.images?.[0]?.url || null,
            slug: property.slug,
            type: property.propertyType || 'PG'
          }
        })
        isOpen.value = suggestions.value.length > 0
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        error.value = 'Failed to fetch suggestions'
        console.error('Search autocomplete error:', err)
      }
    } finally {
      isLoading.value = false
    }
  }

  const debouncedFetch = (searchTerm) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    debounceTimer = setTimeout(() => {
      fetchSuggestions(searchTerm)
    }, debounceMs)
  }

  // Watch query changes and trigger debounced fetch
  watch(query, (newValue) => {
    if (!newValue || newValue.trim().length < minChars) {
      clearSuggestions()
      return
    }
    debouncedFetch(newValue)
  })

  const selectSuggestion = (suggestion) => {
    query.value = suggestion.name
    clearSuggestions()
    return suggestion
  }

  const close = () => {
    isOpen.value = false
  }

  const open = () => {
    if (suggestions.value.length > 0) {
      isOpen.value = true
    }
  }

  return {
    query,
    suggestions,
    isLoading,
    isOpen,
    error,
    selectSuggestion,
    clearSuggestions,
    close,
    open
  }
}
